"use client"

import { configBuilder } from "@/lib/config";
import {OfferConfig, EventType, isUpdateMessage} from "@vertical-insure/shared";
import { isInitMessage, isValidateMessage } from "@vertical-insure/shared";
import { ReactNode, useEffect, useState} from "react"
import { QuoteController } from "@/components/QuoteController";
import { FormProvider, useForm } from "react-hook-form";
import DefaultFormSchema from "@/lib/DefaultFormSchema";
import z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import { datadogRum } from '@datadog/browser-rum';

export const PageController = ({
  children
} : {
  children: ReactNode
}) => { 
  const [config, setConfig] = useState<OfferConfig | undefined>();
  const [offerReady, setOfferReady] = useState(false);

  const schema = DefaultFormSchema
    .superRefine(({products}, ctx) => {
      if (products) {
        for (const [index, product] of products.entries()) {
          if (product.available_quotes > 0
            && product.accepted_quotes.filter((q: string | undefined) => q !== undefined).length === 0
            && product.offer_declined === false
            && product.selection_required === true) {
            ctx.addIssue({
              path: ['products', index],
              code: z.ZodIssueCode.custom,
              message: 'You must either accept or decline.'
            });
          }
        }
      }
    });

  const form = useForm<{ products: [] }>({
    resolver: zodResolver(schema),
    defaultValues: {
      products: []
    }
  });

  useEffect(() => {
    const handler = async (ev: MessageEvent<unknown>) => {
      if (isInitMessage(ev.data)) {
        if (config === undefined) {
          const updatedConfig = configBuilder(ev.data);
          setConfig(updatedConfig);

          datadogRum.setUserProperty("partner_platform.url", updatedConfig.origin);
        }
      }

      if (isValidateMessage(ev.data)) {
        let valid = true;

        if (offerReady) {
          valid = await form.trigger();
        }

        if (config?.origin) {
          window.parent.postMessage({eventType: EventType.validate, is_valid: valid}, config?.origin);
        }

        datadogRum.addAction("validation triggered");
      }

      if (isUpdateMessage(ev.data) && ev.data.product_config) {
        const requestedUpdatedProductConfig = ev.data.product_config;
        const updatedConfig = {
          ...config,
          product_config: requestedUpdatedProductConfig
        } as OfferConfig;

        setConfig(updatedConfig);
      }
    }

    window.addEventListener('message', handler);

    // nosemgrep: wildcard-postmessage-configuration
    window.parent.postMessage({eventType: EventType.ready}, '*');

    return () => window.removeEventListener('message', handler)
  }, [config, offerReady]);

  // Wait until we've received an offer ready state = true before we fire the message
  useEffect(() => {
    if (offerReady) {
      const message = {
        eventType: EventType.offer_state_ready
      }

      window.parent.postMessage(message, '*');

      datadogRum.addAction("offer ready");
    }
  }, [offerReady]);

  const handleOfferReady = (isOfferReady: boolean) => {
    if (offerReady !== isOfferReady) {
      setOfferReady(isOfferReady);
    }
  }
  
  return config ? (
    <main style={config?.styles} className="h-full w-full font-sans">
      {/* Load fonts from google if applicable */}
      {config?.font_url && <style id="google-font-import">{`@import url('${config.font_url}');`}</style>}
      <FormProvider {...form}>
        <QuoteController
          initialConfig={config}
          handleOfferReady={handleOfferReady}
        >
          {children}
        </QuoteController>
      </FormProvider>
      <div id="payment-portal"></div>
    </main>
  ) : null;
}
